<template>
  <div class="modal fade dq-payroll-modal" id="add_overtime" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-modal-titlewrap">
            <div class="dq-hero-icon dq-hero-blue">
              <i class="ti ti-clock"></i>
            </div>
            <div>
              <h4 class="modal-title dq-modal-title mb-0">
                {{ isEditMode ? "Edit Overtime" : "Add Overtime" }}
              </h4>
              <div class="dq-modal-sub">
                Set the overtime type and multiplier used in payroll.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="resetForm"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="onSubmit">
          <div class="modal-body dq-modal-body pb-0">
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-info-circle"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Overtime Details</div>
                    <div class="dq-help">
                      Keep names clear for payroll reports.
                    </div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-12">
                    <label class="form-label dq-label">Name</label>
                    <input
                      v-model="form.name"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. Normal day OT 1.5x"
                      required
                    />
                  </div>

                  <div class="col-md-12">
                    <label class="form-label dq-label">Rate Type</label>
                    <vue-select
                      v-model="form.rate_type"
                      :options="['Hourly', 'Daily']"
                      placeholder="Select rate type"
                      class="dq-vselect"
                    />
                  </div>

                  <div class="col-md-12">
                    <label class="form-label dq-label">Rate Multiplier</label>
                    <div class="input-group dq-input-group">
                      <input
                        v-model.number="form.rate"
                        type="number"
                        min="0"
                        step="0.1"
                        class="form-control dq-input"
                        placeholder="e.g. 1.5, 2, 3"
                        required
                      />
                      <span class="input-group-text dq-addon">x</span>
                    </div>
                    <div class="dq-help mt-1">
                      Example: 1.5 means “time-and-a-half”.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error" class="alert alert-danger my-2 dq-alert">
              {{ error }}
            </div>
            <div v-if="success" class="alert alert-success my-2 dq-alert">
              {{ success }}
            </div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-white border me-2 dq-btn-muted"
              data-bs-dismiss="modal"
              @click="resetForm"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary dq-btn-primary">
              <span
                v-if="overtimeStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ isEditMode ? "Save Changes" : "Add Overtime" }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE MODAL -->
  <div
    class="modal fade dq-confirm-modal"
    id="deleteOvertimeModal"
    tabindex="-1"
    ref="deleteModalEl"
  >
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-confirm-body">
          <div class="dq-confirm-icon">
            <i class="ti ti-trash-x"></i>
          </div>
          <h4 class="mb-1 dq-confirm-title">Confirm Delete</h4>
          <p class="mb-3 dq-confirm-sub">
            This record will be permanently removed. This action cannot be
            undone.
          </p>

          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-light dq-btn-muted" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-danger dq-btn-danger" @click="confirmDelete">
              <span
                v-if="overtimeStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, defineExpose } from "vue";
import { usePayrollOvertimeStore } from "@/stores/payrollOvertimeStore";
import Modal from "bootstrap/js/dist/modal";

const overtimeStore = usePayrollOvertimeStore();

const emit = defineEmits(["refresh"]);

const isEditMode = ref(false);

const form = reactive({
  id: null,
  name: "",
  rate_type: "",
  rate: 0,
});

const error = ref("");
const success = ref("");

function resetForm() {
  form.id = null;
  form.name = "";
  form.rate_type = "";
  form.rate = 0;
  isEditMode.value = false;
  error.value = "";
  success.value = "";
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

async function onSubmit() {
  error.value = "";
  success.value = "";
  if (!form.name || !form.rate_type || !form.rate) {
    error.value = "Please fill all required fields.";
    return;
  }
  try {
    const payload = {
      name: form.name,
      rate_type: form.rate_type,
      rate: form.rate,
    };
    if (!isEditMode.value) {
      await overtimeStore.createOvertime(payload);
      //success.value = "Overtime added!";
    } else {
      await overtimeStore.updateOvertime(form.id, payload);
      //success.value = "Overtime updated!";
    }
    emit("refresh");
    setTimeout(() => {
      closeModal("add_overtime");
      resetForm();
    }, 1000);
  } catch (e) {
    error.value = e?.response?.data?.message || "Failed to save overtime";
  }
}

async function confirmDelete() {
  error.value = "";
  success.value = "";
  try {
    await overtimeStore.deleteOvertime(form.id);
    //success.value = "Overtime deleted!";
    emit("refresh");
    setTimeout(() => {
      closeModal("deleteOvertimeModal");
      resetForm();
    }, 1000);
  } catch (e) {
    error.value = e?.response?.data?.message || "Failed to delete overtime";
  }
}

function openForEdit(overtime) {
  form.id = overtime.id;
  form.name = overtime.name;
  form.rate_type = overtime.rate_type;
  form.rate = overtime.rate;
  isEditMode.value = true;
  error.value = "";
  success.value = "";
}

function openForDelete(overtime) {
  form.id = overtime.id;
  error.value = "";
  const deleteModalEl = ref(null);
  let modalInstance = null;

  deleteModalEl.value = document.getElementById("deleteOvertimeModal");

  if (!modalInstance) {
    modalInstance = new Modal(deleteModalEl.value, { backdrop: "static" });
  }
  modalInstance.show();
}

defineExpose({
  openForEdit,
  resetForm,
  openForDelete,
});
</script>

<style scoped>
/* ===== Shared premium styles (safe & scoped) ===== */
.dq-modal-content {
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 26px 70px rgba(16, 24, 40, 0.18);
}

.dq-modal-header {
  padding: 16px;
  background: radial-gradient(
      800px 260px at 10% 0%,
      rgba(239, 68, 68, 0.14),
      transparent 60%
    ),
    linear-gradient(180deg, #f8fafc, #ffffff);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-modal-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-hero-icon {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-hero-red {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}

.dq-modal-title {
  font-weight: 950;
  letter-spacing: -0.02em;
}

.dq-modal-sub {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-modal-body {
  padding: 14px 16px;
}

.dq-modal-footer {
  padding: 12px 16px;
  background: #f8fafc;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-section {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 18px;
  background: #fff;
  margin-bottom: 12px;
}

.dq-section-head {
  padding: 12px 14px;
  background: #f8fafc;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.dq-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dq-section-badge {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.dq-badge-red {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}

.dq-badge-purple {
  background: rgba(99, 102, 241, 0.12);
  color: rgb(79, 70, 229);
}

.dq-section-body {
  padding: 14px;
}

.dq-h6 {
  font-weight: 950;
}

.dq-help {
  font-size: 0.82rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-label {
  font-weight: 850;
}

.dq-input {
  border-radius: 14px;
  min-height: 44px;
}

.dq-switch-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 16px;
  background: rgba(239, 68, 68, 0.06);
}

.dq-radio-grid {
  display: grid;
  gap: 10px;
}

.dq-radio {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  padding: 10px 12px;
  cursor: pointer;
}

.dq-radio-ui {
  display: flex;
  gap: 10px;
}

.dq-radio-title {
  font-weight: 900;
}

.dq-radio-sub {
  font-size: 0.8rem;
  color: rgba(17, 24, 39, 0.6);
}

.dq-dropdown-btn {
  border-radius: 14px;
  min-height: 44px;
}

.dq-chip {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 800;
}

.dq-btn-primary {
  border-radius: 14px;
}

.dq-btn-muted {
  border-radius: 14px;
}

.dq-confirm-icon {
  width: 64px;
  height: 64px;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  font-size: 26px;
}

.dq-confirm-red {
  background: rgba(239, 68, 68, 0.12);
  color: rgb(220, 38, 38);
}
</style>
